import React, { useEffect } from "react";
import { IConsumptionsFilters } from "../../../../models/Filters/ConsumptionsFilters";
import { Badge, Button, Divider, Popover, PopoverSurface, PopoverTrigger } from "@fluentui/react-components";

// styles
import "./Consumption.List.Filter.Styles.css";

// components
import ConsumptionFilterPanel from "./Consumption.Filter.Panel/Consumption.Filter.Panel.Component";
import ConsumptionItemFilterComponent from "./Consumption.Item.Filter.Component";

// models
import { ProtocolPatient } from "../../../../models/Entities/Protocols/ProtocolPatient";
import { ContractStage } from "../../../../models/Entities/Contracts/ContractStage";
import { ChevronDown16Regular, DismissCircle20Regular, Filter20Filled, Filter20Regular } from "@fluentui/react-icons";
import { ContractConcept } from "../../../../models/Entities/Contracts/ContractConcept";
import { Settlement } from "../../../../models/Entities/Settlements/Settlement";

type IProps = {
  Filters: IConsumptionsFilters;
  ProtocolId: number;
  ContractId: number;
  onChangeFilters: (filters: IConsumptionsFilters) => void;
}


const ConsumptionListFilterComponent = (props: IProps) => {

  // determine if the filter panel is open or closed
  const [isOpen, setIsOpen] = React.useState(false);

  // controls the active-filters dropdown popover
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false);

  // manage the filter panel state — kept in sync with parent
  const [filterProps, setFilterProps] = React.useState<IConsumptionsFilters>(props.Filters);

  // Sync local state whenever the parent's filters change from outside
  useEffect(() => {
    setFilterProps(props.Filters);
  }, [
    props.Filters.FilterText,
    props.Filters.SelectedContractConcepts,
    props.Filters.SelectedContractStages,
    props.Filters.SelectedCurrencys,
    props.Filters.SelectedPractitioners,
    props.Filters.SelectedProtocolPatients,
    props.Filters.SelectedStatus,
    props.Filters.SelectedDateRange,
    props.Filters.SelectedSettlements,
    props.Filters.showGroupedByVisit
  ]);

  const onFilterChange = (filters: IConsumptionsFilters) => {
    setFilterProps(filters);
    props.onChangeFilters(filters);
  }

  const onClearCriteria = (CriteriaName: string) => {
    let currentFilterProps = filterProps;
    switch (CriteriaName) {
      case "Texto":
        currentFilterProps ={
          ...filterProps,
          FilterText: ""
        };
        break;	
      case "Pacientes":
        currentFilterProps = {
          ...filterProps,
          SelectedProtocolPatients: []
        };
        break;
      case "Conceptos":
        currentFilterProps = {
          ...filterProps,
          SelectedContractConcepts: []
        };
        break;
      case "Monedas":
        currentFilterProps ={
          ...filterProps,
          SelectedCurrencys: ""
        };
        break;
      case "Visitas":
        currentFilterProps = {
          ...filterProps,
          SelectedContractStages: []
        };
        break;
      case "Profesionales":
        currentFilterProps = {
          ...filterProps,
          SelectedPractitioners: []
        };
        break;
      case "Estado":
        currentFilterProps = {
          ...filterProps,
          SelectedStatus: []
        };
        break;
      case "Liquidaciones":
        currentFilterProps = {
          ...filterProps,
          SelectedSettlements: []
        };
        break;
      case "Fecha desde":
        currentFilterProps = {
          ...filterProps,
          SelectedDateRange: [undefined, filterProps.SelectedDateRange ? filterProps.SelectedDateRange[1] : undefined]
        };
        break;
      case "Fecha hasta":
        currentFilterProps = {
          ...filterProps,
          SelectedDateRange: [filterProps.SelectedDateRange ? filterProps.SelectedDateRange[0] : undefined, undefined]
        };
        break;
      default:
        break;
    }

    onFilterChange(currentFilterProps);
  }

  const Forms = (
    <ConsumptionFilterPanel
      isOpen={isOpen}
      Filters={filterProps}
      onClose={() => setIsOpen(false)}
      ProtocolId={props.ProtocolId}
      ContractId={props.ContractId}
      onFilterChange={onFilterChange}
    />
  );

  const activeFilterCount = [
    !!filterProps.FilterText,
    (filterProps.SelectedProtocolPatients?.length ?? 0) > 0,
    (filterProps.SelectedContractStages?.length ?? 0) > 0,
    (filterProps.SelectedContractConcepts?.length ?? 0) > 0,
    !!filterProps.SelectedCurrencys,
    (filterProps.SelectedPractitioners?.length ?? 0) > 0,
    (filterProps.SelectedStatus?.length ?? 0) > 0,
    (filterProps.SelectedSettlements?.length ?? 0) > 0,
    !!(filterProps.SelectedDateRange?.[0]),
    !!(filterProps.SelectedDateRange?.[1]),
  ].filter(Boolean).length;

  const onClearAll = () => {
    onFilterChange({
      ...filterProps,
      FilterText: "",
      SelectedProtocolPatients: [],
      SelectedContractConcepts: [],
      SelectedCurrencys: "",
      SelectedContractStages: [],
      SelectedPractitioners: [],
      SelectedStatus: [],
      SelectedSettlements: [],
      SelectedDateRange: [undefined, undefined],
    });
  };

  const filterBadge = (
    <Badge size="small" shape="circular" appearance="filled" color="danger" style={{ marginLeft: '0.4em' }}>
      {activeFilterCount}
    </Badge>
  );

  return (
    <div className="consumption-list-filter">
      <div className="filter-actions">
        {activeFilterCount > 0 ? (
          <div className="filter-split-button-container">
            <Button
              appearance="primary"
              shape="rounded"
              className="filter-split-main"
              icon={<Filter20Filled />}
              onClick={() => setIsOpen(true)}
            >
              Filtros {filterBadge}
            </Button>
            <Popover
              open={isDropdownOpen}
              onOpenChange={(_, data) => setIsDropdownOpen(data.open)}
              positioning="below-end"
            >
              <PopoverTrigger disableButtonEnhancement>
                <Button
                  appearance="primary"
                  shape="rounded"
                  className="filter-split-chevron"
                  icon={<ChevronDown16Regular />}
                />
              </PopoverTrigger>
              <PopoverSurface className="filter-dropdown-surface">
                <div className="filter-dropdown-chips">
                  {filterProps.FilterText && <ConsumptionItemFilterComponent
                    criteriaName="Texto"
                    criteria={[filterProps.FilterText]}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedProtocolPatients?.length > 0 && <ConsumptionItemFilterComponent
                    criteriaName="Pacientes"
                    criteria={filterProps.SelectedProtocolPatients.map((p: ProtocolPatient) => p.numberInProtocol)}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedContractStages?.length > 0 && <ConsumptionItemFilterComponent
                    criteriaName="Visitas"
                    criteria={filterProps.SelectedContractStages.map((s: ContractStage) => s.stageName)}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedContractConcepts?.length > 0 && <ConsumptionItemFilterComponent
                    criteriaName="Conceptos"
                    criteria={filterProps.SelectedContractConcepts.map((c: ContractConcept) => c.concept.conceptName)}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedCurrencys && <ConsumptionItemFilterComponent
                    criteriaName="Monedas"
                    criteria={[filterProps.SelectedCurrencys]}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedPractitioners?.length > 0 && <ConsumptionItemFilterComponent
                    criteriaName="Profesionales"
                    criteria={filterProps.SelectedPractitioners.map((p) => p.fullName)}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedStatus?.length > 0 && <ConsumptionItemFilterComponent
                    criteriaName="Estado"
                    criteria={filterProps.SelectedStatus.map((s) => s.Name)}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedSettlements?.length > 0 && <ConsumptionItemFilterComponent
                    criteriaName="Liquidaciones"
                    criteria={filterProps.SelectedSettlements.map((s: Settlement) => s.name ?? s.identifier)}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedDateRange?.[0] && <ConsumptionItemFilterComponent
                    criteriaName="Fecha desde"
                    criteria={[(filterProps.SelectedDateRange[0] as Date).toLocaleDateString()]}
                    onClearCriteria={onClearCriteria}
                  />}
                  {filterProps.SelectedDateRange?.[1] && <ConsumptionItemFilterComponent
                    criteriaName="Fecha hasta"
                    criteria={[(filterProps.SelectedDateRange[1] as Date).toLocaleDateString()]}
                    onClearCriteria={onClearCriteria}
                  />}
                </div>
                <Divider className="filter-dropdown-divider" />
                <div className="filter-dropdown-footer">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissCircle20Regular />}
                    onClick={() => { onClearAll(); setIsDropdownOpen(false); }}
                  >
                    Limpiar todos
                  </Button>
                </div>
              </PopoverSurface>
            </Popover>
          </div>
        ) : (
          <Button
            appearance="outline"
            shape="rounded"
            icon={<Filter20Regular />}
            onClick={() => setIsOpen(true)}
          >
            Filtros
          </Button>
        )}
      </div>
      {Forms}
    </div>
  );
};

export default ConsumptionListFilterComponent;